import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Landing-system NavBase.
 *
 * Tone-aware sticky header used by every landing surface (Kloutsale,
 * Kloutforce, Katalag). Brand chrome (logo glyph, brand name) and links/
 * actions are passed in as slots so each app keeps its own messaging without
 * duplicating the sticky/border/blur shell.
 *
 * Notes:
 * - tone='light' uses light bg with slate text (Kloutsale brand pattern).
 * - tone='dark'  uses slate-950/80 with white text (Kloutforce/Katalag pattern).
 * - We never inherit color: every text class is explicit.
 */
export type LandingNavTone = 'dark' | 'light';

type Props = {
  tone?: LandingNavTone;
  /** Brand glyph + name (left side). Usually wrapped in a `<Link to="/">`. */
  brand: React.ReactNode;
  /** Center nav (renders inside a pill). Each child is rendered as-is. */
  links?: React.ReactNode;
  /** Right side actions (sign in, primary CTA, etc.). */
  actions?: React.ReactNode;
  className?: string;
};

const toneShell: Record<LandingNavTone, string> = {
  dark: 'border-white/10 bg-slate-950/80 text-white',
  light: 'border-slate-200/80 bg-white/90 text-slate-950',
};

const tonePill: Record<LandingNavTone, string> = {
  dark: 'border-white/10 bg-white/5',
  light: 'border-slate-200 bg-slate-100/80',
};

export default function LandingNavBase({
  tone = 'dark',
  brand,
  links,
  actions,
  className = '',
}: Props) {
  return (
    <header
      className={`sticky top-0 z-50 border-b backdrop-blur-xl ${toneShell[tone]} ${className}`.trim()}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {brand}
        {links ? (
          <nav
            className={`hidden items-center gap-1 rounded-full border p-1 md:flex ${tonePill[tone]}`}
          >
            {links}
          </nav>
        ) : null}
        <div className="flex items-center gap-3">{actions}</div>
      </div>
    </header>
  );
}

/** Convenience link helper used by branded navbars to keep tokens consistent. */
export function LandingNavLink({
  href,
  tone = 'dark',
  children,
}: {
  href: string;
  tone?: LandingNavTone;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'dark'
      ? 'rounded-full px-4 py-2 text-sm text-white/70 transition hover:bg-white/10 hover:text-white'
      : 'rounded-full px-4 py-2 text-sm text-slate-600 transition hover:bg-white hover:text-slate-950';
  // Anchor / external use <a>; routes use <Link>.
  if (href.startsWith('#') || href.startsWith('http') || href.startsWith('mailto:')) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={cls}>
      {children}
    </Link>
  );
}